import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { RecipientGroupSummary } from '@todo/shared';
import { useAuth } from '../hooks/useAuth';
import { Dropdown } from './ui';

/**
 * Who else is invited to a calendar event.
 *
 * A lighter sibling of AssigneePicker: no lead, no department gate, no rank
 * ceiling - a meeting can invite anyone in the org, unlike delegating work.
 * Loads the org's people once and filters in memory, the same tradeoff
 * AssigneePicker makes.
 *
 * RECIPIENT GROUPS are a shortcut on top of the same selection, not a
 * parallel concept. Picking one ADDS its members to whoever is already
 * chosen; individuals can still be added and removed afterwards, and the
 * group itself is never modified from here - a person added to this
 * meeting does not join the group. Groups are edited in Settings only.
 */

interface AttendeePickerProps {
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /**
   * The people and groups of the meeting's ORGANISATION, supplied by the
   * form rather than fetched here.
   *
   * This component used to load the org's people itself, which was fine
   * while there was only ever one org. Now the form has an organisation
   * dropdown and all three pickers must change together when it moves -
   * so the form owns the fetch and every picker reads the same answer.
   * Self-fetching would have meant this one quietly showing the previous
   * org's staff.
   */
  people: Array<{ id: string; displayName: string; jobTitle?: string | null }>;
  groups: RecipientGroupSummary[];
  loading?: boolean;
  /**
   * Fired when a group is applied, so the form can record WHICH group
   * filled the list. Provenance only - the picker still reports the
   * resulting people through `onChange`, and the server never turns a
   * group id back into attendees.
   */
  onGroupApplied?: (groupId: string) => void;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('');
}

export function AttendeePicker({
  selected, onChange, disabled, onGroupApplied, people, groups, loading = false,
}: AttendeePickerProps) {
  const { user } = useAuth();
  /* SUPER_ADMIN, not Admin: only they can create a group now, so only they
     get sent to a page they can actually use. */
  const isSuperAdmin = user?.role.systemKey === 'SUPER_ADMIN';

  /** Which group was last applied, purely so the UI can say so. */
  const [lastApplied, setLastApplied] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  /* A selected id with no matching person means the organisation changed
     under the selection; the form clears it, so this just skips. */
  const chosen = useMemo(
    () => selected
      .map((id) => people.find((p) => p.id === id))
      .filter((p): p is (typeof people)[number] => Boolean(p)),
    [selected, people],
  );

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    return people
      .filter((p) => !selected.includes(p.id))
      .filter((p) =>
        !term ||
        p.displayName.toLowerCase().includes(term));
  }, [people, selected, search]);

  function add(id: string) {
    onChange([...selected, id]);
    setSearch('');
  }

  function remove(id: string) {
    onChange(selected.filter((s) => s !== id));
  }

  /**
   * ADD a group's members to the selection. Never replace, never remove.
   *
   * The Set is what makes it additive and idempotent: applying the same
   * group twice, or two groups that overlap, cannot produce a duplicate -
   * and a duplicate attendee id would be rejected by the attendee table's
   * primary key on save.
   *
   * Members whose account is no longer ACTIVE are skipped: they cannot
   * RSVP and the API would only drop them anyway.
   */
  function applyGroup(group: RecipientGroupSummary) {
    const addable = group.members
      .filter((m) => m.status === 'ACTIVE')
      .map((m) => m.userId);
    onChange([...new Set([...selected, ...addable])]);
    setLastApplied(group.name);
    onGroupApplied?.(group.id);
  }

  return (
    <div>
      <span className="block text-sm font-medium text-slate-700">
        Invite attendees <span className="ml-1 text-slate-400">(optional)</span>
      </span>

      {/*
        With NO groups, this used to render nothing at all - which left the
        one person who can set them up with no way to discover the feature
        exists. They get a one-line pointer; everyone else gets silence,
        because they cannot create one and a dead link is worse than none.
      */}
      {groups.length === 0 && !loading && isSuperAdmin && (
        <p className="mt-1.5 text-xs text-slate-500">
          Invite the same people often?{' '}
          <Link
            to="/recipient-groups"
            className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
          >
            Create a recipient group
          </Link>{' '}
          and add them all in one click.
        </p>
      )}

      {/*
        A FULL-WIDTH SELECT, matching the "+ Add from stakeholders…" control
        directly below it in this same form.

        It was a row of small chips first, which was wrong twice over: it
        read as a caption rather than a control, and it looked like nothing
        else in a form built entirely from full-width fields. Two pickers
        sitting inches apart, doing the same shape of job, now look the
        same - and the one that fills the main list is no longer the
        quieter of the two.
      */}
      {groups.length > 0 && (
        <div className="mt-1.5">
          {/*
            `value` is deliberately always undefined, so the trigger keeps
            reading "Add a group" instead of latching onto the last pick.
            This control performs an ACTION - merge these people into the
            list - rather than holding a value; a group is not a property
            of the meeting, and showing one as if it were would imply the
            event remembers it, which it does not.
          */}
          <Dropdown
            value={undefined}
            placeholder="Add a group"
            disabled={disabled || loading}
            onChange={(id) => {
              const picked = groups.find((g) => g.id === id);
              if (picked) applyGroup(picked);
            }}
            options={groups.map((group) => ({
              value: group.id,
              label: group.name,
              description: `${group.memberCount} ${group.memberCount === 1 ? 'person' : 'people'}`,
            }))}
          />

          {/* The trigger resets to the placeholder, so this line is the
              only confirmation that the pick did anything. */}
          {lastApplied ? (
            <p className="mt-1 text-xs text-emerald-700">
              Added <strong>{lastApplied}</strong>. You can still add or remove
              people below - the group itself is unchanged.
            </p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              Adds everyone in the group to the list below.
            </p>
          )}
        </div>
      )}

      {chosen.length > 0 && (
        <ul className="mt-1.5 space-y-1.5">
          {chosen.map((person) => (
            <li
              key={person.id}
              className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-100 text-[10px] font-semibold text-brand-700">
                {initials(person.displayName)}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
                {person.displayName}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => remove(person.id)}
                aria-label={`Remove ${person.displayName}`}
                className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative mt-1.5">
        <input
          type="text"
          value={search}
          disabled={disabled || loading}
          placeholder={loading ? 'Loading people...' : 'Search by name'}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />

        {open && matches.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {matches.slice(0, 30).map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(person.id)}
                  className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition hover:bg-slate-50"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-100 text-[10px] font-semibold text-brand-700">
                    {initials(person.displayName)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-800">
                      {person.displayName}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {person.jobTitle ?? ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-1 text-xs text-slate-500">
        Invited people get a reminder notification before it starts, and see it on their own calendar.
      </p>
    </div>
  );
}
